import { Drawer } from "@xola/ui-kit";
import { getMeta } from "common";
import parseHtml from "html-react-parser";
import { isString } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import root from "react-shadow";
import { useSeller } from "../../hooks/useAccount";
import { print } from "../../utils/qzTray";
import { shouldUseBrowserPrintDialog } from "@/storages/PrinterStorage";

interface PrinterProps {
    shouldStart: boolean;
    onFinish?: () => void;
    children?: JSX.Element | string | null;
    enablePaperCut?: boolean;
    enableDrawerEject?: boolean;
}

export const Printer = ({
    shouldStart,
    onFinish,
    children,
    enablePaperCut = false,
    enableDrawerEject = false,
}: PrinterProps) => {
    const seller = useSeller();
    const meta = getMeta(seller);
    const [showPreview, setShowPreview] = useState(false);
    const printContainerRef = useRef<HTMLDivElement>(null);
    const isDebug = window.flags?.debug ?? false;
    const useComputerPrintDialog = shouldUseBrowserPrintDialog();

    useEffect(() => {
        if (!shouldStart) {
            return;
        }

        if (isDebug) {
            setShowPreview(true);
        }

        const html = isString(children) ? children : printContainerRef.current?.innerHTML ?? "";

        print(html, getCodes()).then(() => {
            // We're not calling `onFinish` when debug mode is enabled
            // because we're showing the preview drawer which will call `onFinish` when closed
            if (!isDebug) {
                onFinish?.();
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldStart, printContainerRef]);

    const handleClose = () => {
        setShowPreview(false);
        onFinish?.();
    };

    const getCodes = () => {
        const paperCutCode = enablePaperCut && meta.features?.pos?.paperCutCode;
        const drawerEjectCode = enableDrawerEject && meta?.features?.pos?.drawerEjectCode;
        const codes: string[] = [];

        if (paperCutCode) {
            console.log("Paper cut code:", paperCutCode);
            codes.push(paperCutCode);
        }

        if (drawerEjectCode) {
            console.log("Drawer eject code:", drawerEjectCode);
            codes.push(drawerEjectCode);
        }

        return codes;
    };

    const content = isString(children) ? parseHtml(children) : children;
    if (!content) {
        // Do not render anything if there is no content to print
        return null;
    }

    return (
        <>
            {/* We need this always rendered in the tree */}
            {/* We're using the `innerHTML` string to send to the printer */}
            <div ref={printContainerRef} className="hidden">
                {content}
            </div>

            <Drawer
                size={useComputerPrintDialog ? "xl" : "medium"}
                isOpen={showPreview}
                title="Print Receipt Preview"
                // Since we can't use app's CSS for styling the content that is sent to the printer,
                // we're rendering the preview inside a Shadow DOM node to make sure that the content
                // we see in the preview is matching what is sent to te printer as much as possible
                content={<root.div>{content}</root.div>}
                onClose={handleClose}
            />
        </>
    );
};
